import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import StorageIcon from '@mui/icons-material/Storage';
import HttpIcon from '@mui/icons-material/Http';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SpeedIcon from '@mui/icons-material/Speed';
import { directDocumentQuery, getSOWInsightsForPrepare } from '../../utils/aiService';
import { getIntegrationMap, saveIntegrationMap } from '../../utils/preparePhaseService';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

// Interface type icons mapping
const interfaceTypeIcons = {
  'API': <HttpIcon />,
  'File': <StorageIcon />,
  'Database': <StorageIcon />,
  'Middleware': <DeviceHubIcon />,
  'Custom': <ImportExportIcon />
};

// Frequency color mapping
const frequencyColors = {
  'Real-time': 'error',
  'Hourly': 'warning',
  'Daily': 'info',
  'Weekly': 'success',
  'Monthly': 'secondary',
  'Batch': 'primary',
  'On Demand': 'default'
};

const IntegrationMapPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  
  const [integrationMap, setIntegrationMap] = useState({
    overview: '',
    integrations: []
  });
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [primarySource, setPrimarySource] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingIntegrationMap, setEditingIntegrationMap] = useState({
    overview: '',
    integrations: []
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogItem, setDialogItem] = useState({
    id: '',
    name: '',
    source: '',
    target: '',
    description: '',
    interfaceType: 'API',
    frequency: 'Daily',
    dataElements: '',
    notes: ''
  });
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load integration map from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadIntegrationMapFromDB();
    }
  }, [projectId]);
  
  // Load integration map from database
  const loadIntegrationMapFromDB = async () => {
    setLoading(true);
    try {
      const integrationData = await getIntegrationMap(projectId);
      
      if (integrationData && integrationData.overview !== undefined) {
        // We have data in the database
        setIntegrationMap(integrationData);
        setEditingIntegrationMap(integrationData);
      } else {
        // No data in database, set empty state
        const emptyIntegrationMap = {
          overview: '',
          integrations: []
        };
        setIntegrationMap(emptyIntegrationMap);
        setEditingIntegrationMap(emptyIntegrationMap);
      }
    } catch (error) {
      console.error('Error loading integration map from database:', error);
      // Set empty state on error
      const emptyIntegrationMap = {
        overview: '',
        integrations: []
      };
      setIntegrationMap(emptyIntegrationMap);
      setEditingIntegrationMap(emptyIntegrationMap);
    } finally {
      setLoading(false);
    }
  };
  
  
  // Get the SOW document from the project
  const getSowDocument = () => {
    if (project && project.documents && project.documents.sow && project.documents.sow.length > 0) {
      return project.documents.sow[0];
    }
    return null;
  };
  
  // Fetch integration map from project documents and save to database
  const fetchIntegrationMapFromSOW = async () => {
    if (!projectId) return;
    
    // Check if we have a SOW document
    const sowDoc = getSowDocument();
    if (!sowDoc) {
      setSnackbar({
        open: true,
        message: 'No project documents found. Please upload project documents first.',
        severity: 'warning'
      });
      return;
    }
    
    setAiLoading(true);
    setAiResponse(null);
    
    try {
      // For now, use the existing AI insights approach until we add the backend endpoint
      const sowInsightsResult = await getSOWInsightsForPrepare(projectId, 'integration_map');
      
      if (sowInsightsResult.success && sowInsightsResult.data) {
        // Save the data to database
        await saveIntegrationMap(projectId, sowInsightsResult.data);
        
        // Update the integration map state with the fetched and saved data
        setIntegrationMap(sowInsightsResult.data);
        setEditingIntegrationMap(sowInsightsResult.data);
        
        // Store source information
        if (sowInsightsResult.source) {
          setPrimarySource(sowInsightsResult.source);
        }
        
        // Store raw content for reference if available
        if (sowInsightsResult.rawContent) {
          setAiResponse(sowInsightsResult.rawContent);
        }
        
        setSnackbar({
          open: true,
          message: 'Integration map successfully fetched from project documents and saved.',
          severity: 'success'
        });
        
        // Automatically enter edit mode to allow user to review and modify
        setEditMode(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to extract integration map from project documents.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error("Error fetching integration map from project documents:", error);
      setSnackbar({
        open: true,
        message: 'Error fetching integration map from project documents.',
        severity: 'error'
      });
    } finally {
      setAiLoading(false);
    }
  };
  
  
  // Enter edit mode
  const handleEdit = () => {
    setEditingIntegrationMap({...integrationMap});
    setEditMode(true);
  };
  
  // Cancel edit mode
  const handleCancel = () => {
    setEditingIntegrationMap({...integrationMap});
    setEditMode(false);
  };
  
  // Save changes to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveIntegrationMap(projectId, editingIntegrationMap);
      
      setIntegrationMap({...editingIntegrationMap});
      setEditMode(false);
      
      setSnackbar({
        open: true,
        message: 'Integration map saved successfully.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving integration map:', error);
      setSnackbar({
        open: true,
        message: 'Error saving integration map. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle overview change
  const handleOverviewChange = (e) => {
    setEditingIntegrationMap(prevState => ({
      ...prevState,
      overview: e.target.value
    }));
  };
  
  // Open add/edit dialog
  const handleOpenDialog = (index = null) => {
    setDialogItemIndex(index);
    
    if (index !== null) {
      // Edit existing item
      setDialogItem({...editingIntegrationMap.integrations[index]});
    } else {
      // Add new item - generate a new ID
      const newId = generateNewId();
      
      setDialogItem({
        id: newId,
        name: '',
        source: '',
        target: '',
        description: '',
        interfaceType: 'API',
        frequency: 'Daily',
        dataElements: '',
        notes: ''
      });
    }
    
    setOpenDialog(true);
  };
  
  // Generate a new ID
  const generateNewId = () => {
    const existingIds = editingIntegrationMap.integrations.map(item => item.id);
    
    // Find the highest existing ID number and increment by 1
    let highestNum = 0;
    existingIds.forEach(id => {
      const match = id.match(/^INT-(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > highestNum) highestNum = num;
      }
    });
    
    // Format with leading zeros
    const nextNum = highestNum + 1;
    const paddedNum = nextNum.toString().padStart(3, '0');
    
    return `INT-${paddedNum}`;
  };
  
  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogItem({
      id: '',
      name: '',
      source: '',
      target: '',
      description: '',
      interfaceType: 'API',
      frequency: 'Daily',
      dataElements: '',
      notes: ''
    });
  };
  
  // Handle dialog input change
  const handleDialogInputChange = (e) => {
    const { name, value } = e.target;
    setDialogItem(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  // Save dialog item
  const handleSaveDialogItem = () => {
    if (!dialogItem.name.trim() || !dialogItem.id.trim() || !dialogItem.source.trim() || !dialogItem.target.trim()) {
      return;
    }
    
    const updatedIntegrationMap = {...editingIntegrationMap};
    
    if (dialogItemIndex === null) {
      // Add new item
      updatedIntegrationMap.integrations = [...updatedIntegrationMap.integrations, {...dialogItem}];
    } else {
      // Edit existing item
      updatedIntegrationMap.integrations[dialogItemIndex] = {...dialogItem};
    }
    
    setEditingIntegrationMap(updatedIntegrationMap);
    handleCloseDialog();
  };
  
  // Delete item
  const handleDeleteItem = (index) => {
    const updatedIntegrationMap = {...editingIntegrationMap};
    updatedIntegrationMap.integrations = updatedIntegrationMap.integrations.filter((_, i) => i !== index);
    setEditingIntegrationMap(updatedIntegrationMap);
  };
  
  // Render integrations table
  const renderIntegrationsTable = () => {
    const items = editMode ? editingIntegrationMap.integrations : integrationMap.integrations;
    
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell width="8%"><strong>ID</strong></TableCell>
              <TableCell width="15%"><strong>Name</strong></TableCell>
              <TableCell width="12%"><strong>Systems</strong></TableCell>
              <TableCell width="15%"><strong>Description</strong></TableCell>
              <TableCell width="10%"><strong>Interface</strong></TableCell>
              <TableCell width="10%"><strong>Frequency</strong></TableCell>
              <TableCell width="18%"><strong>Data Elements</strong></TableCell>
              {editMode && <TableCell width="12%" align="center"><strong>Actions</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>{item.id || ''}</TableCell>
                <TableCell sx={{ fontWeight: 'medium' }}>{item.name || ''}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.source || ''}
                    </Typography>
                    <ArrowForwardIcon sx={{ mx: 0.5 }} fontSize="small" />
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.target || ''}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {item.description || ''}
                </TableCell>
                <TableCell>
                  <Tooltip title={item.interfaceType}>
                    <Chip
                      icon={interfaceTypeIcons[item.interfaceType || 'Custom'] || <ImportExportIcon />}
                      label={item.interfaceType || 'Custom'}
                      size="small"
                      sx={{ maxWidth: '100%' }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={<SpeedIcon />}
                    label={item.frequency || 'Daily'}
                    color={frequencyColors[item.frequency || 'Daily'] || 'default'}
                    size="small"
                    sx={{ maxWidth: '100%' }}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  <Tooltip title={item.dataElements || ''}>
                    <Typography variant="body2">
                      {item.dataElements || ''}
                    </Typography>
                  </Tooltip>
                </TableCell>
                {editMode && (
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(index)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteItem(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {editMode && (
              <TableRow>
                <TableCell colSpan={editMode ? 8 : 7} align="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    variant="outlined"
                    size="small"
                    sx={{ my: 1 }}
                  >
                    Add Integration Point
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Render notes and details for each integration
  const renderIntegrationNotes = () => {
    const items = editMode ? editingIntegrationMap.integrations : integrationMap.integrations;
    
    return items.map((item, index) => (
      item.notes && (
        <Box key={index} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="primary">
            {item.id}: {item.name} - Notes
          </Typography>
          <Typography variant="body2" paragraph>
            {item.notes}
          </Typography>
          <Divider sx={{ my: 1 }} />
        </Box>
      )
    ));
  };
  
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <SectionTitle variant="h5">
            <DeviceHubIcon />
            Integration Map
          </SectionTitle>
          <Box>
            {editMode ? (
              <>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  sx={{ mr: 1 }}
                >
                  Save
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  startIcon={<AutoAwesomeIcon />}
                  variant="contained"
                  color="primary"
                  onClick={fetchIntegrationMapFromSOW}
                  disabled={aiLoading || !getSowDocument()}
                  sx={{ mr: 1 }}
                >
                  {aiLoading ? 'Fetching...' : 'Fetch From Project Documents'}
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={handleEdit}
                >
                  Edit
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" paragraph>
          Define and document all integration points and requirements between SAP and other systems in your landscape.
        </Typography>
        
        {!getSowDocument() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            No project documents found. Upload project documents to enable AI-assisted content generation.
          </Alert>
        )}
        
        {primarySource && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Content generated from: {primarySource.filename || primarySource.document_name || 'Project Document'}
          </Alert>
        )}
        
        {aiLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Overview */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Integration Approach Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {editMode ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={editingIntegrationMap.overview}
                onChange={handleOverviewChange}
                placeholder="Enter an overview of the integration approach"
              />
            ) : (
              <Typography variant="body1" paragraph>
                {integrationMap.overview}
              </Typography>
            )}
          </CardContent>
        </Card>
        
        {/* Integration Points */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SyncAltIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">
              Integration Points
            </Typography>
          </Box>
          
          {renderIntegrationsTable()}
          
          {/* Integration Notes */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Integration Notes & Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {renderIntegrationNotes()}
          </Box>
        </Box>
      </Paper>
      
      {/* AI Response Accordion (if available) */}
      {aiResponse && (
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>AI-Generated Content (Raw Response)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {aiResponse}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex === null ? 'Add' : 'Edit'} Integration Point
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                name="id"
                label="Integration ID"
                value={dialogItem.id}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                name="name"
                label="Integration Name"
                value={dialogItem.name}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="source"
                label="Source System"
                value={dialogItem.source}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="target"
                label="Target System"
                value={dialogItem.target}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={dialogItem.description}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="interface-type-label">Interface Type</InputLabel>
                <Select
                  labelId="interface-type-label"
                  name="interfaceType"
                  value={dialogItem.interfaceType}
                  onChange={handleDialogInputChange}
                  label="Interface Type"
                >
                  <MenuItem value="API">API</MenuItem>
                  <MenuItem value="File">File</MenuItem>
                  <MenuItem value="Database">Database</MenuItem>
                  <MenuItem value="Middleware">Middleware</MenuItem>
                  <MenuItem value="Custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="frequency-label">Frequency</InputLabel>
                <Select
                  labelId="frequency-label"
                  name="frequency"
                  value={dialogItem.frequency}
                  onChange={handleDialogInputChange}
                  label="Frequency"
                >
                  <MenuItem value="Real-time">Real-time</MenuItem>
                  <MenuItem value="Hourly">Hourly</MenuItem>
                  <MenuItem value="Daily">Daily</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Batch">Batch</MenuItem>
                  <MenuItem value="On Demand">On Demand</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="dataElements"
                label="Data Elements"
                value={dialogItem.dataElements}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={3}
                placeholder="List of data elements to be transferred"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                value={dialogItem.notes}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={4}
                placeholder="Any additional notes or considerations"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveDialogItem} 
            variant="contained" 
            disabled={!dialogItem.name || !dialogItem.id || !dialogItem.source || !dialogItem.target}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntegrationMapPage;
